import { apiEndpoints } from '@/apiEndpoints';
import { Draft } from '@/types/dashboard/draftColumnDef';
import { CreateSupplierData } from '@/types/suppliers/CreateSupplierData';
import { Supplier } from '@/types/suppliers/supplierColumnDef';
import { UpdateSupplierData } from '@/types/suppliers/UpdateSupplierData';
import { queryKeys } from '@/queryKeys';
import {
  keepPreviousData,
  QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import axios from 'axios';

// Toutes les vues qui affichent des fournisseurs : liste, brouillons, filtre
// villes (clé à plat) et liste des prestations (colonne et filtre fournisseur).
const invalidateSupplierLists = (queryClient: QueryClient) => {
  queryClient.invalidateQueries({ queryKey: queryKeys.suppliers.all });
  queryClient.invalidateQueries({ queryKey: queryKeys.drafts.suppliers });
  queryClient.invalidateQueries({ queryKey: queryKeys.citiesBase });
  queryClient.invalidateQueries({ queryKey: queryKeys.benefits.all });
};

const createSupplier = async (supplierData: Partial<CreateSupplierData>) =>
  axios.post(apiEndpoints.suppliers.base, supplierData);

export const useCreateSupplier = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createSupplier,
    onSuccess: () => {
      invalidateSupplierLists(queryClient);
    },
  });
};

const updateSupplier = async (
  supplierId: string,
  supplierData: Partial<UpdateSupplierData>,
) => axios.put(apiEndpoints.suppliers.detail(supplierId), supplierData);

export const useUpdateSupplier = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      supplierId,
      supplierData,
    }: {
      supplierId: string;
      supplierData: Partial<UpdateSupplierData>;
    }) => updateSupplier(supplierId, supplierData),
    onSuccess: () => {
      invalidateSupplierLists(queryClient);
    },
  });
};

const getDraftsSuppliers = async () => {
  const result = await axios.get<Draft[]>(apiEndpoints.suppliers.drafts);
  return result.data;
};

export const useGetDraftsSuppliers = () =>
  useQuery({
    queryKey: queryKeys.drafts.suppliers,
    queryFn: getDraftsSuppliers,
  });

interface SupplierResponse {
  total: number;
  suppliers: Supplier[];
}

interface SupplierParams {
  page?: number;
  pageSize?: number;
  sortField?: string;
  sortOrder?: 'asc' | 'desc';
  filters?: Record<string, string>;
}

const getSuppliers = async ({
  page = 1,
  pageSize = 10,
  sortField = '',
  sortOrder = 'asc',
  filters = {},
}: SupplierParams) => {
  const filtersStr = Object.entries(filters)
    .map(([key, value]) => `${key}:${encodeURIComponent(value)}`)
    .join(',');
  const result = await axios.get<SupplierResponse>(
    apiEndpoints.suppliers.base,
    {
      params: {
        page,
        pageSize,
        sortField,
        sortOrder,
        filters: filtersStr,
      },
    },
  );
  return result.data;
};

export const useGetSuppliers = (params: SupplierParams) =>
  useQuery({
    queryKey: queryKeys.suppliers.list(params),
    queryFn: () => getSuppliers(params),
    placeholderData: keepPreviousData,
  });

type Cities = Array<{
  city: string;
}>;

const getCities = async (country?: string) => {
  const result = await axios.get<Cities>(apiEndpoints.suppliers.cities, {
    params: { country },
  });
  return result.data;
};

export const useGetCities = <TData = Cities>({
  country,
  select,
}: {
  country?: string;
  select?: (data: Cities) => TData;
}) =>
  useQuery({
    queryKey: queryKeys.cities(country),
    queryFn: () => getCities(country),
    select,
  });

type Countries = Array<{
  country: string;
}>;

const getCountries = async () => {
  const result = await axios.get<Countries>(apiEndpoints.suppliers.countries);
  return result.data;
};

export const useGetCountries = <TData = Countries>(
  select?: (data: Countries) => TData,
) =>
  useQuery({
    queryKey: queryKeys.suppliers.countries,
    queryFn: () => getCountries(),
    select,
  });

const deleteSupplier = async (supplierId: string) => {
  const result = await axios.delete(apiEndpoints.suppliers.detail(supplierId));
  return result.data;
};

export const useDeleteSupplier = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ supplierId }: { supplierId: string }) =>
      deleteSupplier(supplierId),
    onSuccess: () => {
      invalidateSupplierLists(queryClient);
    },
  });
};

const deleteMultipleSuppliers = async (supplierIds: string[]) => {
  const result = await axios.delete(apiEndpoints.suppliers.multiple, {
    data: { supplierIds },
  });
  return result.data;
};

export const useDeleteMultipleSuppliers = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ supplierIds }: { supplierIds: string[] }) =>
      deleteMultipleSuppliers(supplierIds),
    onSuccess: () => {
      invalidateSupplierLists(queryClient);
    },
  });
};

const duplicateSupplier = async (supplierId: string, fields: string[]) =>
  axios.post(apiEndpoints.suppliers.duplicate(supplierId), { fields });

export const useDuplicateSupplier = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      supplierId,
      fields,
    }: {
      supplierId: string;
      fields: string[];
    }) => duplicateSupplier(supplierId, fields),
    onSuccess: () => {
      invalidateSupplierLists(queryClient);
    },
  });
};

const findSupplierById = async (supplierId: string) => {
  const result = await axios.get<UpdateSupplierData>(
    apiEndpoints.suppliers.detail(supplierId),
  );
  return result.data;
};

export const useFindSupplierById = (supplierId: string) =>
  useQuery({
    queryKey: queryKeys.suppliers.detail(supplierId),
    queryFn: () => findSupplierById(supplierId),
  });

const getAllMySuppliers = async () => {
  const result = await axios.get<
    Array<{ id: string; name: string; currency: string }>
  >(apiEndpoints.suppliers.allMySuppliers);
  return result.data;
};

export const useGetAllMySuppliers = () =>
  useQuery({
    queryKey: queryKeys.suppliers.allMySuppliers,
    queryFn: () => getAllMySuppliers(),
  });
